import {m as motion} from 'motion/react'
import React from 'react'
import styled, {css} from 'styled-components'

import Button from '@/components/button'
import RichText from '@/components/rich-text'
import {Text, text} from '@/components/text'
import {VisuallyHidden} from '@/components/visually-hidden'
import useCurrency from '@/hooks/use-currency'
import {usePricingCtas} from '@/hooks/use-pricing-ctas'
import checkIconPink from '@/images/icons/check-pink.svg'
import {useGlobalStrings} from '@/providers/global-strings'
import {colors, tokens} from '@/theme'
import getEnvironmentFormConfig from '@/utils/get-environment-field'
import mq from '@/utils/media-query'

import type {ButtonVariant} from '@/components/button'
import type {PricingFragment} from '@/graphql-types'
import type {FormConfig} from '@/types/consents'

// Maximum number of users that can be selected in the Starter plan
const STARTER_USER_CAP = 3

interface PricingProps {
    content: PricingFragment['data']
    mode: BillingCycle
    rawDemoPath?: string
    numberOfUsers: number
}

export const Tiers = ['starter', 'essential', 'advanced', 'beyond'] as const

export enum BillingCycle {
    Monthly = 'monthly',
    Yearly = 'yearly',
}

const PricingTierChildrenCount = 8

export const PricingV2 = ({
    content,
    mode = BillingCycle.Monthly,
    rawDemoPath,
    numberOfUsers,
}: PricingProps) => {
    const {free_plan_cost_label} = useGlobalStrings()
    const {getFormattedCurrency} = useCurrency()

    const formConfig = getEnvironmentFormConfig({
        production: content.form_production,
        staging: content.form_staging,
    })

    const tiers = content.pricing_tiers.flatMap(({pricing_tier}, i) => ({
        ...pricing_tier.document.data,
        tierName: Tiers[i],
    }))

    const isMonthlyCycled = mode === BillingCycle.Monthly

    const handleClick = usePricingCtas({
        formConfig: formConfig as FormConfig,
        signupModalFree: content.signup_modal_free,
        signupModalPro: content.signup_modal_pro,
        rawDemoPath,
    })

    const list = {
        visible: {
            opacity: 1,
            transition: {
                when: 'beforeChildren',
                staggerChildren: 0.1,
            },
        },
        hidden: {opacity: 0},
    }

    const item = {
        visible: {opacity: 1, x: 0},
        hidden: {opacity: 0, x: -30},
    }

    return (
        <Wrapper $columns={tiers.length} initial="hidden" animate="visible" variants={list}>
            {tiers.map(
                (
                    {
                        name,
                        monthly_billing_screen_reader_alt,
                        annual_billing_screen_reader_alt,
                        per_month_text,
                        cost_subtext,
                        monthly_cost_subtext_extra,
                        annual_cost_subtext_extra,
                        excerpt,
                        cta_text,
                        features,
                        features_title,
                        tierName,
                        monthly_billed_cost,
                        annually_billed_cost,
                        monthly_billed_user_cost,
                        annually_billed_user_cost,
                        free_users_included,
                        cashback_text,
                        cta_variant,
                    },
                    index,
                ) => {
                    const isStarter = name.toLowerCase().includes('starter')
                    const isAdvancedAndBeyond = ['advanced', 'beyond'].includes(
                        tierName.toLocaleLowerCase(),
                    )

                    const filteredFeatures = features.filter(
                        ({feature}) => !feature.toLowerCase().includes('cashback'),
                    )

                    const totalMonthlyCost = getTotalMonthlyCost(
                        isMonthlyCycled ? monthly_billed_cost : annually_billed_cost,
                        isMonthlyCycled ? monthly_billed_user_cost : annually_billed_user_cost,
                        free_users_included,
                        isStarter ? Math.min(numberOfUsers, STARTER_USER_CAP) : numberOfUsers,
                    )

                    return (
                        <Tier key={index} variants={item}>
                            <TierName
                                as="h3"
                                $variant="heading-base"
                                style={{marginBottom: tokens.spacing12}}
                            >
                                {name}
                            </TierName>

                            <AmountText>
                                <VisuallyHidden>
                                    {isMonthlyCycled
                                        ? monthly_billing_screen_reader_alt
                                        : annual_billing_screen_reader_alt}
                                </VisuallyHidden>
                                <Text as="span" $color="muted" $variant="paragraph-large">
                                    {totalMonthlyCost === 0
                                        ? free_plan_cost_label
                                        : getFormattedCurrency(totalMonthlyCost)}{' '}
                                    {per_month_text}
                                </Text>
                            </AmountText>

                            <LabelsWrapper>
                                <Text $variant="label-base">{cost_subtext}</Text>

                                <Text $variant="label-base">
                                    {isMonthlyCycled
                                        ? monthly_cost_subtext_extra
                                        : annual_cost_subtext_extra}
                                </Text>

                                {isAdvancedAndBeyond && (
                                    <RichText
                                        content={cashback_text.richText}
                                        $variant="label-base"
                                        $color="brand"
                                    />
                                )}
                            </LabelsWrapper>

                            <Excerpt $variant="paragraph-base" $color="lightMuted">
                                {excerpt}
                            </Excerpt>

                            {content.show_ctas && (
                                <StyledButton
                                    data-testid={`pricing-tier-cta-${name}`}
                                    onClick={() => handleClick(tierName)}
                                    $variant={
                                        cta_variant ? (cta_variant as ButtonVariant) : 'default'
                                    }
                                >
                                    {cta_text}
                                </StyledButton>
                            )}

                            <FeaturesTitle $variant="paragraph-large">
                                {features_title}
                            </FeaturesTitle>

                            <FeaturesList>
                                {filteredFeatures.map(
                                    ({feature, disabled_monthly, is_highlighted}) => (
                                        <FeaturesItem
                                            $isHighlighted={
                                                is_highlighted ||
                                                (disabled_monthly && !isMonthlyCycled)
                                            }
                                            $isDisabled={disabled_monthly && isMonthlyCycled}
                                            key={feature}
                                        >
                                            {feature}
                                        </FeaturesItem>
                                    ),
                                )}
                            </FeaturesList>
                        </Tier>
                    )
                },
            )}
        </Wrapper>
    )
}

const getTotalMonthlyCost = (
    planCost: number,
    userCost: number,
    freeUsers: number,
    numberOfUsers: number,
) => planCost + userCost * Math.max(0, numberOfUsers - freeUsers)

const Wrapper = styled(motion.div)<{$columns: number}>`
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    grid-template-rows: repeat(${PricingTierChildrenCount}, auto);
    row-gap: ${tokens.spacing20};
    padding-top: ${tokens.spacing40};

    ${mq.min('sm')} {
        column-gap: ${tokens.spacing20};
        grid-template-columns: repeat(2, 1fr);
    }

    ${mq.min('md')} {
        row-gap: 0;
        column-gap: ${tokens.spacing2};
        grid-template-columns: repeat(${(props) => props.$columns}, 1fr);
    }

    ${mq.min('lg')} {
        column-gap: ${tokens.spacing20};
    }
`

const Tier = styled(motion.div)`
    display: grid;
    grid-template-rows: subgrid;
    grid-row: span ${PricingTierChildrenCount};
    background-color: ${colors.background.default};
    padding: ${tokens.spacing20};
    row-gap: 0;
`
const TierName = styled(Text)`
    ${mq.min('md')} {
        ${text({$variant: 'title-base'})}
    }
`

const Excerpt = styled(Text)`
    margin-top: ${tokens.spacing32};
    margin-bottom: ${tokens.spacing32};
`

const StyledButton = styled(Button)`
    margin-bottom: ${tokens.spacing32};
    width: 100%;

    ${mq.min('sm')} {
        width: max-content;
    }
`

const FeaturesTitle = styled(Text)`
    ${mq.min('md')} {
        ${text({$variant: 'paragraph-base'})}
    }
`

const FeaturesList = styled.ul`
    display: flex;
    flex-direction: column;
    gap: ${tokens.spacing20};
    padding: 0;
    margin-top: ${tokens.spacing20};
`

const AmountText = styled.div`
    ins {
        text-decoration: none;
    }

    ${mq.min('sm')} {
        margin-bottom: ${tokens.spacing8};
    }
`

const LabelsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${tokens.spacing4};
`

interface FeaturesItemProps {
    $isHighlighted: boolean
    $isDisabled: boolean
}

const FeaturesItem = styled.li<FeaturesItemProps>(({$isHighlighted, $isDisabled}) => {
    const highlightedStyles = css`
        color: ${colors.text.brand};
    `

    const disabledStyles = css`
        color: ${colors.text.extraMuted};
        text-decoration: line-through;

        /* This combination's outcome is #CCC */
        &::before {
            filter: grayscale(1);
            opacity: 0.42;
        }
    `

    return css`
        display: flex;

        ${text({$variant: 'paragraph-base', $color: 'muted'})}

        ${$isHighlighted && highlightedStyles}
        ${$isDisabled && disabledStyles}

        &::before {
            margin-right: ${tokens.spacing12};
            content: url(${checkIconPink});
        }
    `
})
